import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import Loader from '@minecraft.loader';
import ErrorMessage from '@minecraft.error-message';
import { BodyText, FlexCol } from '@minecraft.atoms';
import {
  ExpandableSimpleTable,
  useExpandCellHook,
  SimplePagination,
  usePagination,
  PageHeader,
} from '@minecraft.molecules';
import { TalentScoutCorrespondenceHistoryDoc } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';

export const useTalentScoutCorrespondence = ({ pageNumber, pageSize }) => {
  const { data, loading, error, refetch } = useQuery(TalentScoutCorrespondenceHistoryDoc, {
    variables: {
      page: {
        pageNumber,
        pageSize,
      },
    },
  });

  // Flatten the data to be used by the table
  const flattenedData = data?.getTalentScoutHistory?.data?.map((item) => ({
    ...item,
    stageName: item.profile?.stageName,
    createdBy: `${item.createdBy?.firstName ?? ''} ${item.createdBy?.lastName ?? ''}`,
  }));

  return {
    talentScoutHistoryData: flattenedData,
    totalCount: data?.getTalentScoutHistory.totalCount,
    totalPages: data?.getTalentScoutHistory.totalPages,
    page: data?.getTalentScoutHistory.page,
    loading,
    error,
    refetch,
  };
};

export const TalentScoutHistory = () => {
  const { t, formatIntlRelativeTime } = useTranslation();
  const { pageSize, pageNumber, setPageNumber, setPageSize } = usePagination();

  const { talentScoutHistoryData, totalCount, loading, error, refetch } = useTalentScoutCorrespondence({
    pageNumber,
    pageSize,
  });
  const { expandedCells, toggleExpanded } = useExpandCellHook({ rows: talentScoutHistoryData });

  useEffect(() => {
    // When the page loads, if there's already data in our cache, reload it to make sure we capture any new contacts
    if (talentScoutHistoryData?.length) {
      // Error handling is taken care of by the useQuery hook
      refetch().catch(() => undefined);
    }
  }, []);

  if (error) return <ErrorMessage error={error} />;

  return (
    <FlexCol alignItems="stretch" gap="4">
      <PageHeader sectionName="talent-scout" title={t('reps:talentScout.correspondenceHistory')} />
      {loading ? (
        <Loader />
      ) : (
        <>
          {!talentScoutHistoryData?.length ? (
            <FlexCol alignItems="center" justifyContent="center" className="cn_mt-28">
              <BodyText fontWeight="bold">{t('reps:talentScout.history.noHistory')}</BodyText>
            </FlexCol>
          ) : (
            <FlexCol gap="4" className="cn_mx-4">
              <SimplePagination
                pageNumber={pageNumber}
                pageSize={pageSize}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                totalRecords={totalCount}
              >
                <ExpandableSimpleTable
                  expandedCells={expandedCells}
                  rowIdentifier="stageName"
                  toggleExpanded={toggleExpanded}
                  translations={{
                    expandedButton: (talentName) => t('reps:talentScout.history.expandButton', { talentName }),
                    collapsedButton: (talentName) => t('reps:talentScout.history.collapseButton', { talentName }),
                    tableCaption: t('reps:talentScout.history.tableCaption'),
                    actionsColumnLabel: t('reps:talentScout.history.tableHeader.actions'),
                  }}
                  columnDefs={[
                    {
                      field: 'createdAt',
                      label: t('reps:talentScout.history.tableHeader.sent'),
                      valueFormatter: (value) => formatIntlRelativeTime({ date: value }),
                      flex: 1,
                    },
                    { field: 'stageName', label: t('reps:talentScout.history.tableHeader.talent'), flex: 1 },
                    {
                      field: 'message',
                      label: t('reps:talentScout.history.tableHeader.message'),
                      flex: 4,
                      expandable: true,
                    },
                    { field: 'createdBy', label: t('reps:talentScout.history.tableHeader.contactedBy'), flex: 1 },
                  ]}
                  data={talentScoutHistoryData}
                />
              </SimplePagination>
            </FlexCol>
          )}
        </>
      )}
    </FlexCol>
  );
};
